import { createContext, useContext, useEffect, type ReactNode } from 'react';

import { type FooterRailId } from './footer-rail.constants';

/**
 * One collapsed item's row. `label` is pre-formatted by the owning component so
 * these files never re-mint or move a (path-keyed) message id.
 */
export interface FooterMoreRowDescriptor {
  id: string;
  label: string;
  icon: ReactNode;
  testId: string;
  /** Set → link row; otherwise `onSelect` drives a button row. */
  href?: string;
  /** Opens `href` in a new tab (Get Started, Discovery) instead of routing in-app. */
  external?: boolean;
  onSelect?: () => void;
  isActive?: boolean;
}

/** Stable identity, so a collapsible's registration effect isn't re-run by every decision. */
export interface FooterRailRegistry {
  /**
   * Membership in the overflow decision. Kept free of the descriptor so a
   * nav-driven descriptor change (e.g. `isActive`) doesn't re-register the item
   * and churn `orderedIds`, which would force a re-measure and flicker.
   */
  register: (id: FooterRailId, pinned: boolean) => void;
  unregister: (id: FooterRailId) => void;
  /** Updates the row descriptor (label, icon, isActive) without touching membership. */
  setDescriptor: (id: FooterRailId, descriptor: FooterMoreRowDescriptor) => void;
  /**
   * Removes the row descriptor. Owned by the descriptor effect's own cleanup, kept
   * out of `unregister` so a membership-only change (a `pinned` flip) can't drop the
   * descriptor — which otherwise vanishes an item that later collapses (e.g. Remote
   * after a device unlinks).
   */
  removeDescriptor: (id: FooterRailId) => void;
  /**
   * Supplies the translated "More" label from a component that already holds
   * `useTranslations` (FooterIconButtons) — the tile itself renders in a footer
   * file that can't add a `t()` site, and the i18next catalog is frozen.
   */
  setMoreLabel: (label: string) => void;
}

const noop = () => undefined;

export const FooterRailRegistryContext = createContext<FooterRailRegistry>({
  register: noop,
  unregister: noop,
  setDescriptor: noop,
  removeDescriptor: noop,
  setMoreLabel: noop,
});

export const useFooterRailRegistry = (): FooterRailRegistry =>
  useContext(FooterRailRegistryContext);

export interface FooterRailState {
  moreIds: readonly FooterRailId[];
  isCollapsed: (id: FooterRailId) => boolean;
  /** Collapsed items in display order, for the More tile. */
  moreDescriptors: readonly FooterMoreRowDescriptor[];
  /** Translated "More" (tooltip + aria-label). Defaulted until FooterIconButtons sets it. */
  moreLabel: string;
}

export const FooterRailStateContext = createContext<FooterRailState>({
  moreIds: [],
  isCollapsed: () => false,
  moreDescriptors: [],
  moreLabel: 'More',
});

export const useFooterRailState = (): FooterRailState =>
  useContext(FooterRailStateContext);

/**
 * Registers a collapsible footer item and returns whether it's currently collapsed
 * (rendered by the More instead of in place). The caller MUST memoize `descriptor`
 * — the descriptor effect keys on it, so an unstable value loops.
 *
 * Membership and descriptor are registered separately on purpose: membership
 * (id + pinned) drives the overflow decision, so keeping the frequently-changing
 * descriptor (`isActive` flips on every navigation) out of it means a nav doesn't
 * re-register the item and re-measure — which otherwise flickers.
 */
export function useFooterRailCollapsible({
  id,
  visible,
  pinned,
  descriptor,
}: {
  id: FooterRailId;
  visible: boolean;
  pinned: boolean;
  descriptor: FooterMoreRowDescriptor;
}): boolean {
  const registry = useFooterRailRegistry();
  const { isCollapsed } = useFooterRailState();

  useEffect(() => {
    if (!visible) return;
    registry.register(id, pinned);
    return () => registry.unregister(id);
  }, [registry, id, visible, pinned]);

  useEffect(() => {
    if (!visible) return;
    registry.setDescriptor(id, descriptor);
    return () => registry.removeDescriptor(id);
  }, [registry, id, visible, descriptor]);

  return visible && isCollapsed(id);
}
